import type { RGBA, TRBLNumberCOORDS } from '../types';
import type { BackgroundImageMode, BackgroundPosition } from '../../types/layout';
import { ICON_STYLE_REGULAR } from '../../utils/constants';

/**
 * Default props for Page element
 * Based on packages/shared/components/Page/index.tsx
 * Uses global style color reference for backgroundColor
 */
export const PAGE_DEFAULTS = {
  fillSpace: 'yes' as const,
  padding: [0, 0, 0, 0] as TRBLNumberCOORDS,
  backgroundColor: '$ref:#/colors/color6',
  radius: 0,
  maxWidth: '100%',
  height: 'auto',
  gap: 0,
  bgImage: null as string | null,
  bgImageScale: 100,
  bgImageOpacity: 100,
  bgImageMode: 'fill' as BackgroundImageMode,
  bgImagePosition: 'center center' as BackgroundPosition,
  bgVideoSourceType: 'url' as const,
  bgVideoUrl: undefined as string | undefined,
  bgVideoProvider: undefined as 'youtube' | 'vimeo' | undefined,
  bgVideoThumbnailUrl: undefined as string | undefined,
  bgVideoEmbedCode: undefined as string | undefined,
  bgVideoMode: 'fill' as const,
  bgVideoOpacity: 100,
  tablet: {} as Record<string, unknown>,
  mobile: {} as Record<string, unknown>,
};

/**
 * Default props for Container element (section/row/column)
 * Based on packages/shared/components/Container/index.tsx
 */
export const CONTAINER_DEFAULTS = {
  flexDirection: 'column' as const,
  alignItems: 'center' as const,
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  height: 'auto',
  minHeight: 200,
  minWidth: 100,
  maxWidth: 1200,
  maxWidthUnit: 'px' as const,
  disableMaxWidth: false,
  padding: [10, 10, 10, 10] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  backgroundColor: undefined as RGBA | undefined,
  borderColor: undefined as RGBA | undefined,
  hoverBorderColor: undefined as RGBA | undefined,
  borderWidth: 0,
  borderRadius: [0, 0, 0, 0] as TRBLNumberCOORDS,
  shadow: 0,
  opacity: 1,
  overflow: 'visible' as const,
  bgImage: undefined as string | undefined,
  bgImageScale: 100,
  bgImageOpacity: 100,
  bgImageMode: 'fill' as BackgroundImageMode,
  bgImagePosition: 'center center' as BackgroundPosition,
  bgVideoSourceType: 'url' as const,
  bgVideoUrl: undefined as string | undefined,
  bgVideoProvider: undefined as 'youtube' | 'vimeo' | undefined,
  bgVideoThumbnailUrl: undefined as string | undefined,
  bgVideoEmbedCode: undefined as string | undefined,
  bgVideoMode: 'fill' as const,
  bgVideoOpacity: 100,
  position: 'relative' as const,
  zIndex: 0,
  fillSpace: 'no' as const,
  flexBasis: '100%',
  flexGrow: 0,
  placeholderHeight: 512,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },
  aspectRatioLock: false,
  aspectRatio: undefined as number | undefined,
  tablet: {
    padding: [20, 20, 20, 20] as TRBLNumberCOORDS,
  },
  mobile: {
    padding: [10, 10, 10, 10] as TRBLNumberCOORDS,
    flexDirection: 'column' as const,
    width: '100%',
    fillSpace: 'no' as const,
    flexBasis: '100%',
  },
};

/**
 * Section-specific defaults (top-level container)
 */
export const SECTION_DEFAULTS = {
  ...CONTAINER_DEFAULTS,
  flexDirection: 'column' as const,
  padding: [40, 40, 40, 40] as TRBLNumberCOORDS,
  fillSpace: 'no' as const,
  disableMaxWidth: true, // use full width sections
};

/**
 * Row-specific defaults (horizontal container)
 */
export const ROW_DEFAULTS = {
  ...CONTAINER_DEFAULTS,
  flexDirection: 'row' as const,
  justifyContent: 'center',
  alignItems: 'flex-start',
  fillSpace: 'no' as const,
};

/**
 * Column-specific defaults (vertical container in a row)
 */
export const COLUMN_DEFAULTS = {
  ...CONTAINER_DEFAULTS,
  flexDirection: 'column' as const,
  fillSpace: 'yes' as const,
  flexBasis: '100%',
};

/**
 * Default props for Button element
 * Based on packages/shared/components/Button/index.tsx
 */
export const BUTTON_DEFAULTS = {
  text: 'Button',
  buttonVariant: 'primary' as const,
  buttonSize: 'medium' as const,
  fillWidth: false,
  appliedGlobalStyle: true,
  fontSize: '16px',
  fontWeight: '400',
  fontFamily: 'Inter',
  fontStyle: 'normal' as const,
  color: { r: 255, g: 255, b: 255, a: 1 } as RGBA,
  backgroundColor: { r: 0, g: 0, b: 0, a: 1 } as RGBA,
  padding: [6, 16, 6, 16] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderRadius: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderWidth: 0,
  opacity: 1,
  textAlign: 'center' as const,
  letterSpacing: 0,
  buttonType: 'square' as const,
  position: 'relative' as const,
  top: '0px',
  left: '0px',
  right: '',
  bottom: '',
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  zIndex: 0,
  minWidth: '64px',
  minHeight: '32px',
  width: 'auto',
  height: 'auto',
  flexBasis: 'auto',
  fillSpace: 'no' as const,
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },
};

/**
 * Default props for Image element
 * Based on packages/shared/components/Image/index.tsx
 */
export const IMAGE_DEFAULTS = {
  src: '',
  alt: '',
  fitType: 'fill' as const,
  width: '100%',
  height: '512px',
  padding: [0, 0, 0, 0] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderRadius: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderWidth: 0,
  hoverBorderColor: undefined as RGBA | undefined,
  opacity: 1,
  objectPosition: 'center top',
  backgroundPosition: 'center top' as BackgroundPosition,
  backgroundSize: '100%',
  position: 'relative' as const,
  top: '0px',
  left: '0px',
  right: undefined as string | undefined,
  bottom: undefined as string | undefined,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  zIndex: 0,
  minWidth: 0,
  minHeight: 0,
  maxWidth: 1200,
  maxWidthUnit: 'px' as const,
  fillSpace: 'yes' as const,
  flexBasis: '100%',
  flexGrow: 0,
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },
  aspectRatioLock: false,
  aspectRatio: undefined as number | undefined,
  tablet: {} as Record<string, unknown>,
  mobile: {
    width: '100%',
    fillSpace: 'no' as const,
    flexBasis: '100%',
  },
};

/**
 * Default props for Icon element
 * Based on packages/shared/components/Icon/index.tsx
 */
export const ICON_DEFAULTS = {
  iconName: 'star',
  iconStyle: ICON_STYLE_REGULAR,
  color: { r: 0, g: 0, b: 0, a: 1 } as RGBA,
  opacity: 1,
  width: '48px',
  height: '48px',
  aspectRatio: 1,
  aspectRatioLock: true,
  padding: [0, 0, 0, 0] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderRadius: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderWidth: 0,
  position: 'relative' as const,
  top: '0px',
  left: '0px',
  right: undefined as string | undefined,
  bottom: undefined as string | undefined,
  positionAnchorsState: {
    enableTop: true,
    enableRight: false,
    enableBottom: false,
    enableLeft: true,
    enabled: false,
  },
  zIndex: 0,
  fillSpace: 'no' as const,
  flexBasis: 'auto',
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },
  tablet: {} as Record<string, unknown>,
  mobile: {} as Record<string, unknown>,
};

/**
 * Default props for Video element
 * Based on packages/shared/components/Video/index.tsx
 */
export const VIDEO_DEFAULTS = {
  sourceType: 'url' as const,
  url: '',
  embedCode: '',
  videoProvider: 'youtube' as const,
  thumbnailUrl: undefined as string | undefined,
  autoplay: false,
  loop: false,
  mute: false,
  controls: true,
  fitType: 'fill' as const,
  width: '100%',
  height: '315px',
  padding: [0, 0, 0, 0] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderRadius: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderWidth: 0,
  opacity: 1,
  position: 'relative' as const,
  top: '0px',
  left: '0px',
  right: undefined as string | undefined,
  bottom: undefined as string | undefined,
  positionAnchorsState: {
    enableTop: true,
    enableRight: false,
    enableBottom: false,
    enableLeft: true,
    enabled: false,
  },
  zIndex: 0,
  minWidth: 0,
  minHeight: 0,
  fillSpace: 'yes' as const,
  flexBasis: '100%',
  flexGrow: 0,
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },
  aspectRatioLock: true,
  tablet: {} as Record<string, unknown>,
  mobile: {
    width: '100%',
    fillSpace: 'no' as const,
    flexBasis: '100%',
  },
};

/**
 * Default props for Countdown element
 * Based on packages/shared/components/Countdown/index.tsx
 * Uses global style color references for text colors
 */
export const COUNTDOWN_DEFAULTS = {
  // Countdown mode
  mode: 'standard' as const,
  dateTime: '', // Will be dynamically generated at transform time
  evergreenId: '', // Will be dynamically generated at transform time
  evergreenDuration: 604800, // 1 week in seconds
  type: 'default' as const,

  // Unit visibility
  showDays: true,
  showHours: true,
  showMinutes: true,
  showSeconds: true,

  // Custom labels
  labelDays: 'Days',
  labelHours: 'Hours',
  labelMinutes: 'Minutes',
  labelSeconds: 'Seconds',

  // Number typography
  numberFontFamily: 'Inter',
  numberFontWeight: '700',
  numberFontStyle: 'normal',
  numberFontSize: '32px',
  numberLetterSpacing: 0,
  numberTextColor: '$ref:#/colors/color4', // Global style text color

  // Label typography
  labelFontFamily: 'Inter',
  labelFontWeight: '400',
  labelFontStyle: 'normal',
  labelFontSize: '12px',
  labelLetterSpacing: 0.05,
  labelTextColor: '$ref:#/colors/color4', // Global style text color

  // Layout
  gap: 16,
  width: '100%',
  height: 'auto',
  minHeight: 64,
  padding: [16, 24, 16, 24] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderRadius: [8, 8, 8, 8] as TRBLNumberCOORDS,
  borderWidth: 0,
  opacity: 1,

  // Position
  position: 'relative' as const,
  top: '0px',
  left: '0px',
  right: undefined as string | undefined,
  bottom: undefined as string | undefined,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  zIndex: 0,

  // Flex
  fillSpace: 'yes' as const,
  flexBasis: '100%',
  flexGrow: 0,

  // Visibility
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },

  // Events (will be built at transform time)
  // clickEvent and expireEvent are built from XML attributes

  // Mobile breakpoint defaults
  mobile: {
    width: '100%',
    fillSpace: 'no' as const,
    flexBasis: '100%',
  },
};

/**
 * Default props for Form element
 * Based on packages/shared/components/Form/index.tsx
 * Uses global style color references for form colors
 */
export const FORM_DEFAULTS = {
  id: '',
  name: 'Contact Form',
  followupAction: { type: 'remain' as const },
  fieldWidth: '100%',
  fieldMaxWidth: '100%',
  fieldMinWidth: '0px',
  fieldMargin: [0, 0, 16, 0] as TRBLNumberCOORDS,
  fieldBorderRadius: [6, 6, 6, 6] as TRBLNumberCOORDS,
  fieldInputPadding: [6, 8, 6, 8] as TRBLNumberCOORDS,
  fieldLabelFontFamily: 'Inter',
  fieldLabelFontSize: '10px',
  fieldLabelFontWeight: 500,
  fieldLabelColor: '$ref:#/colors/color4',
  fieldLabelInvalidColor: { r: 234, g: 101, b: 89, a: 1 } as RGBA,
  fieldLabelSpacing: 0,
  fieldInputFontFamily: 'Inter',
  fieldInputFontSize: '12px',
  fieldInputFontWeight: 500,
  fieldInputTextColor: '$ref:#/colors/color4',
  fieldInputHoverTextColor: undefined as string | undefined,
  fieldInputSpacing: 0,
  fieldInputBackgroundColor: '$ref:#/colors/color6',
  fieldInputBorderColor: '$ref:#/colors/color5',
  fieldInputBorderWidth: 1,
  fieldInputHoverBackgroundColor: undefined as string | undefined,
  fieldInputHoverBorderColor: undefined as string | undefined,
  fieldInputInvalidBackgroundColor: { r: 255, g: 255, b: 255, a: 1 } as RGBA,
  fieldInputInvalidBorderColor: { r: 234, g: 101, b: 89, a: 1 } as RGBA,
  fieldHelperTextColor: { r: 0, g: 0, b: 0, a: 1 } as RGBA,
  fieldHelperTextInvalidColor: { r: 234, g: 101, b: 89, a: 1 } as RGBA,
  fieldCheckboxCheckedColor: '$ref:#/colors/color1',
  fieldShadowType: 'none' as const,
  fieldShadowSettings: { x: 0, y: 0, blur: 0, spread: 0, color: '#000000' },
  width: '100%',
  height: 'auto',
  minWidth: 100,
  minHeight: 64,
  maxWidthUnit: 'px' as const,
  maxWidth: 600,
  padding: [16, 16, 16, 16] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderRadius: [8, 8, 8, 8] as TRBLNumberCOORDS,
  borderWidth: 1,
  borderColor: '$ref:#/colors/color5',
  hoverBorderColor: undefined as string | undefined,
  backgroundColor: undefined as string | undefined,
  position: 'relative' as const,
  top: '0px',
  left: '0px',
  right: undefined as string | undefined,
  bottom: undefined as string | undefined,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  zIndex: 0,
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },
  aspectRatioLock: false,
  aspectRatio: undefined as number | undefined,
  mobile: {
    width: '100%',
    fillSpace: 'no' as const,
    flexBasis: '100%',
  },
};

/**
 * Default props for Form Text Field
 * Based on packages/shared/components/Form/fields/FormTextField.tsx
 */
export const FORM_TEXT_FIELD_DEFAULTS = {
  id: '',
  inputType: 'text' as const,
  label: 'Text',
  showLabel: true,
  placeholder: 'Name',
  required: false,
  hidden: false,
  helpText: '',
  showHelpText: false,
  enforceCharacterLimit: false,
  minCharacterLimit: null as number | null,
  maxCharacterLimit: null as number | null,
  urlMapping: '',
};

/**
 * Default props for Form Email Field
 * Based on packages/shared/components/Form/fields/FormEmailField.tsx
 */
export const FORM_EMAIL_FIELD_DEFAULTS = {
  ...FORM_TEXT_FIELD_DEFAULTS,
  inputType: 'email' as const,
  label: 'Email',
  placeholder: 'Email',
  primaryEmailField: false,
};

/**
 * Default props for Form Phone Field
 * Based on packages/shared/components/Form/fields/FormPhoneField.tsx
 */
export const FORM_PHONE_FIELD_DEFAULTS = {
  id: '',
  label: 'Phone Number',
  showLabel: true,
  placeholder: 'Phone',
  required: false,
  hidden: false,
  helpText: '',
  showHelpText: false,
};

/**
 * Default props for Form Dropdown Field
 * Based on packages/shared/components/Form/fields/FormDropdownField.tsx
 */
export const FORM_DROPDOWN_FIELD_DEFAULTS = {
  id: '',
  label: 'Dropdown',
  showLabel: true,
  inputType: 'dropdown' as const,
  required: false,
  hidden: false,
  urlMapping: '',
  placeholder: 'Select Option',
  options: ['Option one', 'Option two', 'Option three', 'Option four'] as string[],
  defaultOptions: [] as string[],
  isSingleSelect: true,
  helpText: '',
  showHelpText: false,
};

/**
 * Default props for Form Selection Field
 * Based on packages/shared/components/Form/fields/FormSelectionField.tsx
 */
export const FORM_SELECTION_FIELD_DEFAULTS = {
  id: '',
  label: 'Selection',
  inputType: 'selection' as const,
  required: false,
  hidden: false,
  placeholder: 'Select one or more options:',
  options: FORM_DROPDOWN_FIELD_DEFAULTS.options,
  defaultOptions: [] as string[],
  isSingleSelect: false,
  helpText: '',
  showHelpText: false,
};

/**
 * Default props for Form Consent Field
 * Based on packages/shared/components/Form/fields/FormConsentField.tsx
 */
export const FORM_CONSENT_FIELD_DEFAULTS = {
  id: '',
  label: 'Consent',
  inputType: 'consent' as const,
  text: 'Yes, I consent to receiving emails',
  required: false,
  hidden: false,
  selectedByDefault: false,
  showDescription: true,
  description:
    'This form collects information we will use to send you updates about promotions, special offers, and news. We will not share or sell your personal information. You can unsubscribe at any time.',
  showUrlLink: false,
  linkUrl: '',
  linkText: '',
  selectedLinkVariant: 'link1' as const,
};

/**
 * Default props for Form Submit Button
 * Based on packages/shared/components/Form/buttons/FormSubmitButton.tsx
 */
export const FORM_SUBMIT_BUTTON_DEFAULTS = {
  text: 'Submit',
  variant: 'primary' as const,
  size: 'medium' as const,
  fullWidth: true,
};

/**
 * Default props for Text element
 * Based on packages/shared/components/Text/index.tsx
 * Note: Text styling comes from global styles (appliedGlobalStyle: true)
 */
export const TEXT_DEFAULTS = {
  // Layout props
  textAlign: 'left',
  padding: [0, 0, 0, 0] as TRBLNumberCOORDS,
  margin: [0, 0, 0, 0] as TRBLNumberCOORDS,
  borderRadius: [0, 0, 0, 0] as TRBLNumberCOORDS,
  maxWidth: 1200,
  maxWidthUnit: 'px' as const,
  width: '100%',
  height: 'auto',
  fillSpace: 'yes' as const,
  lineHeight: 1.5,
  backgroundColor: undefined as RGBA | undefined,
  // Global style props (always enabled for DSL-generated text)
  appliedGlobalStyle: true,
  selectedStyleVariant: 'heading.h2' as const,
  // Other required defaults
  shadow: 0,
  position: 'relative' as const,
  zIndex: 0,
  top: '',
  left: '',
  right: '',
  bottom: '',
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  visibility: {
    desktop: true,
    tablet: true,
    mobile: true,
  },
};

/**
 * Context-aware width/flexBasis/fillSpace defaults
 */
export type ContextAwareDefaults = {
  width: string;
  flexBasis: string;
  fillSpace: 'yes' | 'no';
};

/**
 * Mobile breakpoint defaults for width/flexBasis/fillSpace
 * Mobile always uses full width with no flex growing
 */
export const MOBILE_WIDTH_DEFAULTS: ContextAwareDefaults = {
  width: '100%',
  flexBasis: '100%',
  fillSpace: 'no',
};

/**
 * Calculate context-aware width/flexBasis/fillSpace defaults based on parent direction
 * and sibling configuration.
 *
 * Priority:
 * 1. Explicit width from XML attribute
 * 2. Pre-calculated width from parent (for mixed explicit/auto scenarios)
 * 3. Equal split based on sibling count (row-direction parent only)
 * 4. Full width (column-direction parent or no parent context)
 *
 * @param parentFlexDirection - Parent container's flex direction
 * @param explicitWidth - Explicit width from XML attribute (if provided)
 * @param calculatedWidth - Pre-calculated width from parent (for mixed explicit/auto scenarios)
 * @returns Context-aware defaults for width, flexBasis, fillSpace
 */
export const getContextAwareDefaults = (
  parentFlexDirection: 'row' | 'column' | undefined,
  explicitWidth: string | undefined,
  calculatedWidth: string | undefined,
): ContextAwareDefaults => {
  // Priority 1: Explicit width from XML attribute
  // In column-direction parents, children should NOT fill (grow) vertically —
  // they should take their natural height. Use width: 100% and set flexBasis
  // to the explicit width so elements can constrain themselves via flexBasis.
  if (explicitWidth) {
    const isColumnParent = parentFlexDirection === 'column' || parentFlexDirection === undefined;

    if (isColumnParent) {
      // Column parent: width stays 100%, flexBasis controls the constraint
      return {
        width: '100%',
        flexBasis: explicitWidth,
        fillSpace: 'no',
      };
    }

    // Row parent: use explicit width for both, always allow flex-grow
    return {
      width: explicitWidth,
      flexBasis: explicitWidth,
      fillSpace: 'yes',
    };
  }

  // Priority 2: Pre-calculated width from parent (for mixed explicit/auto scenarios)
  if (calculatedWidth) {
    return {
      width: calculatedWidth,
      flexBasis: calculatedWidth,
      fillSpace: 'yes',
    };
  }

  // Priority 3 & 4: No explicit width - use defaults based on parent direction
  // For row-direction parent with no pre-calculated width, this shouldn't happen
  // (parent should always pre-calculate), but fall back to 100% to be safe
  if (parentFlexDirection === 'row') {
    return {
      width: '100%',
      flexBasis: '100%',
      fillSpace: 'yes',
    };
  }

  // Column-direction parent or no parent context: full width, no fill
  return {
    width: '100%',
    flexBasis: '100%',
    fillSpace: 'no',
  };
};

/**
 * Determine fillSpace for leaf elements (text, image, video, form) based on parent direction.
 *
 * Mirrors the builder's setFlexDirection behavior (useContainerHelper.ts):
 * - column parent → children get fillSpace: 'no' (don't stretch vertically)
 * - row parent → children get fillSpace: 'yes' (fill horizontal space)
 * - no parent context → use the element's own default
 */
export const getLeafFillSpace = (
  parentFlexDirection: 'row' | 'column' | undefined,
  defaultValue: 'yes' | 'no',
): 'yes' | 'no' => {
  if (!parentFlexDirection) return defaultValue;
  return parentFlexDirection === 'column' ? 'no' : 'yes';
};
